// SCR-STAFF-404 — استردادها و تأییدها (US-BIL-052، 093): فهرست با وضعیت (در انتظار تأیید، در حال پردازش، انجام، ناموفق، رد)، تأیید یا رد مدیر
// (bil.refund.approve؛ C-AGENT-554)، ثبت مرجع کارتخوان یا انتقال برای تکمیل، تلاش دوباره استرداد ناموفق با روش دیگر.
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { unwrap, type BilRefundStatus, type BilRefundView } from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, ErrorState, FormDialog, JalaliDate, ListPage, type DataTableColumn } from '@phoenix/ui';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useConnection } from '../../offline/OfflineProvider';
import { Amount, bilKeys, RefundStatusChip, SCR_STAFF_404 } from './common';

const STATUSES: readonly BilRefundStatus[] = ['PendingApproval', 'Processing', 'Completed', 'Failed', 'Rejected'];

export function RefundsPage() {
  const { t } = useTranslation('bil');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const connection = useConnection();
  const queryClient = useQueryClient();
  const key = useIdempotencyKey();
  const [status, setStatus] = useState<BilRefundStatus | ''>('PendingApproval');
  const [completing, setCompleting] = useState<BilRefundView | null>(null);
  const [reference, setReference] = useState('');
  const refunds = useInfiniteQuery({
    queryKey: bilKeys.refunds(session?.tenantId, status),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => unwrap(tenantApi.GET('/api/v1/billing/refunds', { params: { query: { status: status || undefined, cursor: pageParam, limit: 50 } } })),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const done = () => {
    setCompleting(null);
    setReference('');
    void queryClient.invalidateQueries({ queryKey: bilKeys.all(session?.tenantId) });
  };
  const action = useMutation({
    mutationFn: ({ id, kind }: { id: string; kind: 'approve' | 'reject' | 'complete' | 'retry' }) => {
      const headers = { 'Idempotency-Key': key.current() };
      const path = { path: { id } };
      switch (kind) {
        case 'approve':
          return unwrap(tenantApi.POST('/api/v1/billing/refunds/{id}:approve', { params: path, headers }));
        case 'reject':
          return unwrap(tenantApi.POST('/api/v1/billing/refunds/{id}:reject', { params: path, headers }));
        case 'retry':
          return unwrap(tenantApi.POST('/api/v1/billing/refunds/{id}:retry', { params: path, body: { method: 'Cash' }, headers }));
        default:
          return unwrap(tenantApi.POST('/api/v1/billing/refunds/{id}:complete', { params: path, body: { reference: reference.trim() || null }, headers }));
      }
    },
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: done,
  });
  // BR-BIL-055 (AP-OFF-01): استرداد و تأیید آن در حالت آفلاین غیرفعال است.
  const full = access.accessMode === 'Full' && connection.status === 'online';
  const canApprove = full && access.can('bil.refund.approve');
  const canProcess = full && access.can('bil.refund.create');
  const columns: DataTableColumn<BilRefundView>[] = [
    { id: 'date', headerKey: 'bil:refunds.columns.date', cell: (row) => <JalaliDate value={row.requestedAt} withTime /> },
    { id: 'patient', headerKey: 'bil:refunds.columns.patient', cell: (row) => row.patientName ?? '—' },
    { id: 'amount', headerKey: 'bil:refunds.columns.amount', cell: (row) => <Amount money={row.amount} /> },
    { id: 'method', headerKey: 'bil:refunds.columns.method', cell: (row) => t(`bil:method.${row.method}`) },
    { id: 'source', headerKey: 'bil:refunds.columns.source', cell: (row) => t(`bil:credit_source.${row.source}`) },
    { id: 'status', headerKey: 'bil:refunds.columns.status', cell: (row) => <RefundStatusChip status={row.status} /> },
    {
      id: 'actions',
      headerKey: 'bil:refunds.columns.actions',
      cell: (row) => (
        <Stack direction="row" spacing={1}>
          {row.status === 'PendingApproval' && canApprove ? (
            <>
              <Button size="small" onClick={() => action.mutate({ id: row.id, kind: 'approve' })} data-testid="bil-refund-approve">
                {t('bil:refunds.approve')}
              </Button>
              <Button size="small" color="error" onClick={() => action.mutate({ id: row.id, kind: 'reject' })} data-testid="bil-refund-reject">
                {t('bil:refunds.reject')}
              </Button>
            </>
          ) : null}
          {row.status === 'Processing' && canProcess ? (
            <Button size="small" onClick={() => setCompleting(row)} data-testid="bil-refund-complete">
              {t('bil:refunds.complete')}
            </Button>
          ) : null}
          {row.status === 'Failed' && canProcess ? (
            <Button size="small" onClick={() => action.mutate({ id: row.id, kind: 'retry' })} data-testid="bil-refund-retry">
              {t('bil:refunds.retry_cash')}
            </Button>
          ) : null}
        </Stack>
      ),
    },
  ];
  return (
    <ListPage
      screenId={SCR_STAFF_404}
      titleKey="bil:refunds.title"
      subtitleKey="bil:refunds.subtitle"
      filters={
        <TextField select size="small" label={t('bil:refunds.columns.status')} value={status} onChange={(event) => setStatus(event.target.value as BilRefundStatus | '')} sx={{ minWidth: 220 }} slotProps={{ htmlInput: { 'data-testid': 'bil-refunds-status' } }}>
          <MenuItem value="">{t('bil:invoices.all')}</MenuItem>
          {STATUSES.map((value) => (
            <MenuItem key={value} value={value}>
              {t(`bil:refund_status.${value}`)}
            </MenuItem>
          ))}
        </TextField>
      }
    >
      {action.error && !completing ? <ErrorState error={action.error} compact /> : null}
      <DataTable
        captionKey="bil:refunds.title"
        columns={columns}
        rows={refunds.data?.pages.flatMap((page) => page.items)}
        getRowId={(row) => row.id}
        isLoading={refunds.isPending}
        error={refunds.error}
        onRetry={() => void refunds.refetch()}
        hasMore={refunds.hasNextPage}
        onLoadMore={() => void refunds.fetchNextPage()}
        isFetchingMore={refunds.isFetchingNextPage}
        emptyTitleKey="bil:refunds.empty"
      />
      <FormDialog
        open={completing !== null}
        titleKey="bil:refunds.complete"
        onSubmit={() => completing && action.mutate({ id: completing.id, kind: 'complete' })}
        onClose={() => setCompleting(null)}
        isDirty={reference !== ''}
        isSubmitting={action.isPending}
        submitError={action.error}
      >
        <TextField
          sx={{ mt: 1 }}
          fullWidth
          label={t('bil:refunds.reference')}
          value={reference}
          onChange={(event) => setReference(event.target.value)}
          helperText={completing ? t(`bil:method.${completing.method}`) : undefined}
          slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'bil-refund-reference' } }}
        />
      </FormDialog>
    </ListPage>
  );
}
